import { useMutation, useSuspenseQuery } from '@tanstack/react-query';

import type { MutationConfig, QueryConfig } from '@/lib/react-query';
import { api, extractData } from '@/lib/treaty';

export type ShadowmapShare = {
  token: string;
  createdAt: string;
  expiryDate: string;
  additionalData?: string | null;
};

const shareShadowmap = async ({
  shadowmapId,
  tenantId,
  expiresIn = 30,
}: {
  shadowmapId: string;
  tenantId: string;
  expiresIn?: number;
}) => extractData(api.shadowmaps.share.post({ shadowmapId, tenantId, expiresIn }));

export const useShareShadowmap = ({
  mutationConfig,
}: {
  mutationConfig?: MutationConfig<typeof shareShadowmap>;
} = {}) => {
  return useMutation({ ...mutationConfig, mutationFn: shareShadowmap });
};

const getShadowmapShares = async ({
  shadowmapId,
  tenantId,
}: {
  shadowmapId: string;
  tenantId: string;
}) => extractData(api.shadowmaps({ shadowmapId }).get({ query: { tenantId } }));

export const getShadowmapSharesOptions = (shadowmapId: string, tenantId: string) => ({
  queryKey: ['shadowmap-share', shadowmapId, tenantId],
  queryFn: () => getShadowmapShares({ shadowmapId, tenantId }),
});

export const useGetShadowmapShares = ({
  queryConfig,
  shadowmapId,
  tenantId,
}: {
  queryConfig?: QueryConfig<typeof getShadowmapShares>;
  shadowmapId: string;
  tenantId: string;
}) => {
  return useSuspenseQuery({ ...queryConfig, ...getShadowmapSharesOptions(shadowmapId, tenantId) });
};

const revokeShadowmapShare = async ({ token }: { token: string }) =>
  extractData(api.shadowmaps({ shadowmapId: token }).delete());

export const useRevokeShadowmapShare = ({
  mutationConfig,
}: {
  mutationConfig?: MutationConfig<typeof revokeShadowmapShare>;
} = {}) => {
  return useMutation({ ...mutationConfig, mutationFn: revokeShadowmapShare });
};
